import { Subscribable, Subscription } from '@benzinga/subscribable';
import { NewsSocket, NewsSocketEvent } from './socket';
import { FeedId, NewsfeedFieldType, NewsBZQLQuery, SocketStory } from './entities';
import { SafePromise, SafeType } from '@benzinga/safe-await';
import { Expression } from '@benzinga/session';
import { DateTime } from 'luxon';

interface NewsSessionOpen {
  type: 'news:open';
}

interface NewsSessionClose {
  type: 'news:close';
}
interface NewsReconnect {
  type: 'news:reconnected' | 'news:disconnected';
}

interface NewsSessionStories {
  stories: SocketStory[];
  type: 'news:new_stories' | 'news:future_historic_stories' | 'news:past_historic_stories' | 'news:reconnect_stories';
}

type HistoricCheckpoint = {
  CreatedAt: { date: string; operator: 'gte' | 'lte' | 'gt' | 'lt' };
  offset: number;
};
export type NewsSessionEvents = NewsSessionOpen | NewsSessionClose | NewsReconnect | NewsSessionStories;

export class NewsSession extends Subscribable<NewsSessionEvents> {
  private socket: NewsSocket;
  private feedId: FeedId;
  private query: NewsBZQLQuery;
  private socketSubscription?: Subscription<NewsSocket>;

  private liveStoryInProgress?: SocketStory;
  private oldLiveStoryInProgress?: SocketStory;
  private reconnectHistoricStoryInProgress?: SocketStory;
  private liveQueryInProgress?: SafePromise<SocketStory[]>;
  private pastHistoricCheckpoint?: HistoricCheckpoint;
  private pastHistoricQueryInProgress?: SafePromise<SocketStory[]>;
  private futureHistoricCheckpoint?: HistoricCheckpoint;
  private futureHistoricQueryInProgress?: SafePromise<SocketStory[]>;
  private isFeedOpen = false;

  constructor(newsfeedSocket: NewsSocket, query: NewsBZQLQuery, feedId: FeedId, jumpedToDate?: DateTime) {
    super();
    this.socket = newsfeedSocket;
    this.feedId = feedId;
    this.query = query;
    this.pastHistoricCheckpoint = jumpedToDate
      ? { CreatedAt: { date: jumpedToDate?.toISO() || '', operator: 'lte' }, offset: 0 }
      : undefined;
    this.futureHistoricCheckpoint = jumpedToDate
      ? { CreatedAt: { date: jumpedToDate?.toISO() || '', operator: 'gt' }, offset: 0 }
      : undefined;
  }

  public getFeedId = (): FeedId => this.feedId;
  public getQuery = (): NewsBZQLQuery => this.query;

  public getPastHistoric = async (numStoriesRequested = 100): SafePromise<SocketStory[]> => {
    if (this.pastHistoricQueryInProgress === undefined) {
      let query: NewsBZQLQuery = {
        ...this.query,
        limit: numStoriesRequested,
        sort: { CreatedAt: -1 },
      };
      if (this.pastHistoricCheckpoint) {
        const createAtExpression = Expression.fromToken<NewsfeedFieldType>([
          'CreatedAt',
          this.pastHistoricCheckpoint.CreatedAt.operator,
          this.pastHistoricCheckpoint.CreatedAt.date,
        ]);
        query = {
          ...query,
          offset: (this.query.offset ?? 0) + this.pastHistoricCheckpoint.offset,
          where: this.query.where ? createAtExpression.and(this.query.where) : null,
        };
      }

      const getStories = async (): SafePromise<SocketStory[]> => {
        const stories = await this.socket.query(query);
        if (stories.err) {
          return stories;
        } else {
          const pastCheckpoint = this.pastHistoricCheckpoint;
          const pastStories = stories.ok;
          if (pastStories.length > 0) {
            const lastStory = pastStories[pastStories.length - 1];
            this.pastHistoricCheckpoint = {
              CreatedAt: { date: lastStory.CreatedAt, operator: 'lte' },
              offset:
                lastStory.CreatedAt === pastCheckpoint?.CreatedAt.date && pastCheckpoint?.CreatedAt.operator === 'lte'
                  ? pastCheckpoint.offset + numStoriesRequested
                  : 0,
            };
            if (this.liveStoryInProgress === undefined) this.liveStoryInProgress = pastStories[0];
          }

          this.dispatch({ stories: pastStories, type: 'news:past_historic_stories' });
          return { ok: pastStories };
        }
      };
      this.pastHistoricQueryInProgress = getStories();
      const awaitedStories = await this.pastHistoricQueryInProgress;
      this.pastHistoricQueryInProgress = undefined;

      return awaitedStories;
    } else {
      return this.pastHistoricQueryInProgress;
    }
  };

  public getFutureHistoric = async (numStoriesRequested = 100): SafePromise<SocketStory[]> => {
    if (this.futureHistoricQueryInProgress === undefined) {
      let query: NewsBZQLQuery = {
        ...this.query,
        limit: numStoriesRequested,
        sort: { CreatedAt: 1 },
      };
      if (this.futureHistoricCheckpoint) {
        const createAtExpression = Expression.fromToken<NewsfeedFieldType>([
          'CreatedAt',
          this.futureHistoricCheckpoint.CreatedAt.operator,
          this.futureHistoricCheckpoint.CreatedAt.date,
        ]);
        query = {
          ...query,
          offset: (this.query.offset ?? 0) + this.futureHistoricCheckpoint.offset,
          where: this.query.where ? createAtExpression.and(this.query.where) : null,
        };
      }

      const getStories = async (): SafePromise<SocketStory[]> => {
        const stories = await this.socket.query(query);
        if (stories.err) {
          return stories;
        } else {
          const futureCheckpoint = this.futureHistoricCheckpoint;
          const futureStories = stories.ok;
          if (futureStories.length > 0) {
            const firstStory = futureStories[futureStories.length - 1];

            this.futureHistoricCheckpoint = {
              CreatedAt: { date: firstStory.CreatedAt, operator: 'gte' },
              offset:
                firstStory.CreatedAt === futureCheckpoint?.CreatedAt.date &&
                futureCheckpoint.CreatedAt.operator === 'gte'
                  ? futureCheckpoint.offset + numStoriesRequested
                  : 0,
            };
          }
          this.dispatch({ stories: futureStories, type: 'news:future_historic_stories' });
          return { ok: futureStories };
        }
      };

      this.futureHistoricQueryInProgress = getStories();
      const awaitedStories = await this.futureHistoricQueryInProgress;
      this.futureHistoricQueryInProgress = undefined;
      return awaitedStories as SafeType<SocketStory[]>;
    }
    return this.futureHistoricQueryInProgress as SafePromise<SocketStory[]>;
  };

  protected onReconnect = async (): SafePromise<SocketStory[]> => {
    if (this.liveQueryInProgress === undefined) {
      if (this.oldLiveStoryInProgress === undefined) {
        return { ok: [] };
      }
      const createdAtExpression = Expression.fromToken<NewsfeedFieldType>([
        'CreatedAt',
        'gte',
        this.oldLiveStoryInProgress.CreatedAt,
      ]);
      const query = {
        ...this.query,
        limit: 10000,
        where: this.query.where ? createdAtExpression.and(this.query.where) : createdAtExpression,
      };
      if (this.reconnectHistoricStoryInProgress) {
        query.where.and(
          Expression.fromToken<NewsfeedFieldType>([
            'CreatedAt',
            'lte',
            this.reconnectHistoricStoryInProgress.CreatedAt,
          ]),
        );
      }

      const getStories = async (): SafePromise<SocketStory[]> => {
        const stories = await this.socket.query(query);
        if (stories.err) {
          return stories;
        } else {
          let requestStories = stories.ok;
          if (this.oldLiveStoryInProgress) {
            let done = false;
            let foundStory = this.reconnectHistoricStoryInProgress === undefined;
            requestStories = stories.ok.filter(story => {
              if (this.reconnectHistoricStoryInProgress?.NodeID === story.NodeID) {
                foundStory = true;
                return false;
              } else if (this.oldLiveStoryInProgress?.NodeID === story.NodeID) {
                foundStory = false;
                done = true;
                return false;
              }
              return foundStory;
            });
            if (done) {
              this.reconnectHistoricStoryInProgress = undefined;
              this.oldLiveStoryInProgress = undefined;
            } else {
              this.reconnectHistoricStoryInProgress = requestStories[requestStories.length - 1];
              this.onReconnect();
            }
          }
          this.dispatch({ stories: requestStories, type: 'news:reconnect_stories' });
          return { ok: requestStories };
        }
      };

      this.liveQueryInProgress = getStories();
      const awaitedStories = await this.liveQueryInProgress;
      this.liveQueryInProgress = undefined;
      // did we time out? if so try again
      if (awaitedStories.err === undefined && awaitedStories.ok === undefined) {
        return this.onReconnect();
      }
      return awaitedStories as SafeType<SocketStory[]>;
    }
    return this.liveQueryInProgress as SafePromise<SocketStory[]>;
  };

  protected onFirstSubscription(): void {
    this.socketSubscription = this.socket.subscribe(this.socketCallback);
    this.socket.startFeed(this);
    this.isFeedOpen = true;
  }

  protected onZeroSubscriptions(): void {
    this.socket.endFeed(this);
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.isFeedOpen = false;
  }

  private socketCallback = (event: NewsSocketEvent): void => {
    switch (event.type) {
      case 'news:subscribe':
        if (event.feedId === this.feedId) {
          this.dispatch({ type: 'news:open' });
        }
        break;
      case 'news:unsubscribe':
        if (event.feedId === this.feedId) {
          this.dispatch({ type: 'news:close' });
        }
        break;
      case 'news:subscription_stories':
        if (event.feedIds?.some(x => this.feedId === x)) {
          this.liveStoryInProgress = event.story;
          this.dispatch({ stories: [event.story], type: 'news:new_stories' });
        }
        break;
      case 'news:reconnected':
        if (this.isFeedOpen) {
          this.oldLiveStoryInProgress = this.liveStoryInProgress;
          this.socket.startFeed(this);
          this.onReconnect();
          this.dispatch({ type: 'news:reconnected' });
        }
        break;
      case 'news:disconnected':
        this.reconnectHistoricStoryInProgress = undefined;
        this.oldLiveStoryInProgress = undefined;
        if (this.isFeedOpen) {
          this.dispatch({ type: 'news:disconnected' });
        }
        break;
    }
  };
}
